import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  TextField,
  Grid,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Checkbox,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { red } from '@mui/material/colors';
import uniqBy from 'lodash/uniqBy';

import { FULL_API_URL } from 'utils';
import { downloadService } from 'services';
import { FileUploader, Alert, TextEditor } from 'ui';
import { LinkButton, RoundButton } from 'ui/Buttons';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { matchFileFormat } from '../shared/validations';
import { initializeInputs, checkDocuments, shouldBeRemoved } from '../shared/state';
import { createProduct } from '../services';
import { fetchShopProductsAction, fetchShopProductPropertiesAction } from '../store/shopActions';
import { getProductsProperties } from '../selectors/shopSelectors';
import { ShopProduct } from './ShopProduct/ShopProduct';
import { ShopSelectionList } from './ShopSelectionList/ShopSelectionList';
import { ShopProductDocumentsModal } from './ShopProductDocumentsModal';
import { documentInputFields, documentTypes } from '../shared/state/documentsUploader';

const useStyle = makeStyles((theme) => ({
  input: {
    width: '100%',
  },
  button: {
    minWidth: 120,
    marginLeft: 10,
  },
  finishIcon: {
    width: 150,
    height: 150,
    color: theme.palette.primary.main,
  },
  container: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  error: {
    color: red[500],
  },
  documentsButton: {
    textTransform: 'none',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    borderRadius: 16,
    height: 32,
    marginRight: theme.spacing(1),
  },
  documentButtonToRemove: {
    borderColor: red[500],
    color: red[500],
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  description: Yup.string().required(),
  price: Yup.number().nullable(),
  activationFee: Yup.number().nullable(),
  isFreeTrial: Yup.bool(),
  active: Yup.bool(),
  orderFormInput: Yup.mixed().test('fileFormat', 'Order form have unsupported format', (value) =>
    matchFileFormat(value),
  ),
  productDescriptionInput: Yup.mixed().test(
    'fileFormat',
    'Product description have unsupported format',
    (value) => matchFileFormat(value),
  ),
  termsAndConditionsInput: Yup.mixed().test(
    'fileFormat',
    'Terms & conditions have unsupported format',
    (value) => matchFileFormat(value),
  ),
});

const columns = [
  'name',
  'category',
  'region',
  'segment',
  'productLines',
  'industry',
  'source',
  'pricing',
  'validity',
  'amount',
];

const getInitialValues = (product) =>
  product
    ? {
        name: product.name,
        description: product.description,
        price: product.pricing && product.pricing.length > 0 ? product.pricing[0].price : undefined,
        activationFee:
          product.pricing && product.pricing.length > 0
            ? product.pricing[0].activationFee
            : undefined,
        isFreeTrial: product.isFreeTrial,
        active: product.active,
        marketLabel: product.marketLabel || undefined,
        ...initializeInputs,
      }
    : { ...initializeInputs };

const ShopPanelPackageCreator = ({ product: editedProduct }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();

  const products = useSelector(({ shop }) => shop.products);
  const productProperties = useSelector(getProductsProperties);

  const [productsToAddIds, setProductsToAddIds] = useState([]);
  const [productsToRemove, setProductsToRemove] = useState([]);

  const [addedProducts, setAddedProducts] = useState(
    editedProduct
      ? editedProduct.packageProducts.map((packageProduct) =>
          products.data.find(({ id }) => id === packageProduct.id),
        )
      : [],
  );

  const initialValues = getInitialValues(editedProduct);

  const [isLoading, setIsLoading] = useState(false);

  const steps = [
    t('Select products'),
    editedProduct ? t('Edit package properties') : t('Specify package properties'),
    t('Preview'),
    t('Finish'),
  ];

  const [activeStep, setActiveStep] = React.useState(editedProduct ? 1 : 0);

  const [product, setProduct] = useState(null);
  const [productPreview, setProductPreview] = useState(null);

  const [multiplePrice, setMultiplePrice] = useState(false);
  const [prices, setPrices] = useState([]);
  const [activationFees, setActivationFees] = useState([]);

  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  const [productImage, setProductImage] = useState(null);

  useEffect(() => {
    dispatch(fetchShopProductPropertiesAction());
    dispatch(fetchShopProductsAction());

    if (!editedProduct) return;

    const isMultiplePrice = editedProduct.pricing.length > 1;

    if (!isMultiplePrice) return;

    setMultiplePrice(isMultiplePrice);
    setPrices(editedProduct.pricing.map(({ price }) => price));
    setActivationFees(editedProduct.pricing.map(({ activationFee }) => activationFee));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDocumentsModal = () => setShowDocumentsModal(!showDocumentsModal);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getDocumentsByType = (prod, values, documentType) => {
    const document = prod ? prod.documents.find(({ type }) => type === documentType) : null;
    const toRemove = shouldBeRemoved(values, documentType);

    return !toRemove && document ? [{ name: document.name }] : [];
  };

  const getLogoUrl = () => {
    if (productImage) {
      return productImage.preview;
    }

    return editedProduct ? editedProduct.logo : null;
  };

  const handleSubmit = (values, { validateForm }) => {
    validateForm(values);

    if (!productImage && !editedProduct) return;
    if (
      multiplePrice &&
      (prices.some((price) => typeof price === 'undefined') ||
        activationFees.some((fee) => typeof fee === 'undefined' || fee === null))
    )
      return;

    const productValues = {
      categories: [],
      regions: [],
      segments: [],
      industries: [],
      sources: [],
    };

    addedProducts.forEach((addedProduct) => {
      Object.keys(productValues).forEach((key) => {
        if (key === 'industries') {
          productValues[key] = uniqBy([...productValues[key], ...addedProduct[key]], 'name');
        } else {
          const currentValues = productValues[key];
          productValues[key] = [...new Set([...currentValues, ...addedProduct[key]])];
        }
      });
    });

    const allProductLines = addedProducts.map(({ productLines }) => productLines[0]);

    const inputDocuments = [
      ...(values.orderFormInput
        ? [{ name: values.orderFormInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.orderForm)),
      ...(values.productDescriptionInput
        ? [{ name: values.productDescriptionInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.productDescription)),
      ...(values.termsAndConditionsInput
        ? [{ name: values.termsAndConditionsInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.terms)),
    ];

    setProductPreview({
      id: 0,
      products: productProperties.data.productLines.map(({ name }) => ({
        type: name,
        availability: allProductLines.includes(name),
        upgrade: false,
      })),
      pricing: multiplePrice
        ? prices.map((tierPrice, index) => ({
            price: tierPrice,
            tier: index + 1,
            activationFee: activationFees[index],
            active: true,
          }))
        : [
            {
              tier: null,
              price: values.price,
              activationFee: values.activationFee,
              active: true,
            },
          ],
      name: values.name,
      description: values.description,
      ...productValues,
      isPackage: true,
      packageProducts: addedProducts.map(({ id, productLines }) => ({ id, type: productLines[0] })),
      units: 0,
      documents: inputDocuments,
      purchased: false,
      marketLabel: values.marketLabel,
      isFreeTrial: values.isFreeTrial,
      ...(typeof values.isFreeTrial && { trialPeriod: 14 }),
      productLines: [],
      logo: getLogoUrl(),
      cancellationPeriod: 30,
      validityDays: 365,
    });

    setProduct({
      name: values.name,
      description: values.description,
      packageProducts: addedProducts.map(({ id }) => id).join(','),
      pricing: multiplePrice
        ? prices.map((tierPrice, index) => ({
            price: tierPrice,
            tier: index + 1,
            activationFee: activationFees[index],
          }))
        : [
            {
              tier: null,
              price: values.price,
              activationFee: values.activationFee,
            },
          ],
      isFreeTrial: values.isFreeTrial,
      active: values.active,
      marketLabel: values.marketLabel,
      ...(typeof values.orderFormInput !== 'undefined' && { orderForm: values.orderFormInput }),
      ...(typeof values.productDescriptionInput !== 'undefined' && {
        productDescription: values.productDescriptionInput,
      }),
      ...(typeof values.termsAndConditionsInput !== 'undefined' && {
        terms: values.termsAndConditionsInput,
      }),
    });

    handleNext();
  };

  const handleChangeTierPrice = (value, tierIndex) => {
    const pricesCopy = [...prices];

    pricesCopy[tierIndex] = value ? Number(value) : undefined;

    setPrices(pricesCopy);
  };

  const handleChangeTierFee = (value, tierIndex) => {
    const feesCopy = [...activationFees];

    feesCopy[tierIndex] = value ? Number(value) : undefined;

    setActivationFees(feesCopy);
  };

  const create = () => {
    setIsLoading(true);

    const formData = new FormData();

    // eslint-disable-next-line
    for (const field in product) {
      const value = product[field];
      formData.append(field, field === 'pricing' ? JSON.stringify(value) : value);
    }

    if (productImage) {
      formData.append('logo', productImage, productImage.name);
    }

    if (editedProduct) {
      formData.append('id', editedProduct.id);
    }

    createProduct(formData)
      .then(() => {
        handleNext();
      })
      .catch((error) =>
        dispatch(
          addNotificationAction({
            message: error.message,
            variant: 'error',
          }),
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddProducts = () => {
    const addedProductsIds = addedProducts.map(({ id }) => id);
    const productsToAdd = products.data.filter(
      ({ id }) => productsToAddIds.includes(id) && !addedProductsIds.includes(id),
    );

    const productsToAddCategories = [
      ...new Set(productsToAdd.map(({ categories }) => categories[0])),
    ];
    const addedProductsCategories = addedProducts.map(({ categories }) => categories[0]);

    // prevent from adding products with different categories
    if (
      productsToAddCategories.length > 1 ||
      (addedProducts.length &&
        productsToAddCategories.length &&
        !addedProductsCategories.includes(productsToAddCategories[0]))
    ) {
      dispatch(
        addNotificationAction({
          message: t('Package cannot contain products with different categories'),
          variant: 'error',
        }),
      );
      return;
    }

    setAddedProducts([
      ...addedProducts,
      ...products.data.filter(
        ({ id }) => productsToAddIds.includes(id) && !addedProductsIds.includes(id),
      ),
    ]);

    setProductsToAddIds([]);
  };

  const handleRemoveProducts = () => {
    setAddedProducts(addedProducts.filter(({ id }) => !productsToRemove.includes(id)));
  };

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('Failed to get document. Please try again or contact.'),
      }),
    );

  const downloadDocument = (id, name) => {
    downloadService({
      path: `/shop/file-download/${id}/`,
      name,
      onError: throwFetchError,
    });
  };

  const removeDocument = (type, setFieldValue) => {
    const inputField = documentInputFields[type];
    if (inputField) {
      setFieldValue(documentInputFields[type], null);
    }
    editedProduct.documents = editedProduct.documents
      ? editedProduct.documents.filter((document) => document.type !== type)
      : [];
  };

  return (
    <>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={editedProduct && index === 0}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {editedProduct && !editedProduct.ableToEdit && (
        <Box mt={4}>
          <Alert message={t('This package is already purchased and cannot be edited')} />
        </Box>
      )}
      {activeStep === 0 && (
        <>
          <Box mt={2} mb={2}>
            <Typography variant="h5">{t('Select and add products to package:')}</Typography>
          </Box>
          <ShopSelectionList
            options={{ search: true }}
            products={products.data}
            onSelectionChange={(items) => setProductsToAddIds(items)}
            toolbar={
              <Button
                onClick={handleAddProducts}
                color="primary"
                variant="contained"
                size="small"
                disabled={!productsToAddIds.length}
              >
                {t('Add products')}
              </Button>
            }
            columns={columns}
            editMode
          />
          <Box mt={2} p={2} className={classes.header}>
            <Typography variant="h5">{t('Added products:')}</Typography>
          </Box>
          <Box className={classes.container}>
            <ShopSelectionList
              products={addedProducts}
              onSelectionChange={(items) => setProductsToRemove(items)}
              toolbar={
                <Button
                  onClick={handleRemoveProducts}
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={!productsToRemove.length}
                >
                  {t('Remove products')}
                </Button>
              }
              columns={columns}
              editMode
            />
          </Box>
          <Box mt={2}>
            <Grid container justifyContent="flex-end">
              <Button
                color="primary"
                variant="contained"
                className={classes.button}
                disabled={addedProducts.length < 2}
                onClick={handleNext}
              >
                {t('Next')}
              </Button>
            </Grid>
          </Box>
        </>
      )}
      <Box display={activeStep === 1 ? 'block' : 'none'}>
        <Box mt={2}>
          <Typography variant="h5">{t('Selected products:')}</Typography>
        </Box>
        <ShopSelectionList
          options={{ selectableRows: false }}
          products={addedProducts}
          onSelectionChange={(items) => setProductsToRemove(items)}
          columns={columns}
          editMode
        />
        <Box mt={6} mb={2}>
          <Typography variant="h5">{t('Specify package properties:')}</Typography>
        </Box>
        <Formik
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          initialValues={initialValues}
        >
          {({ handleChange, setFieldValue, values, errors }) => (
            <Form>
              <Box width="75%" margin="0 auto">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label={t('Name')}
                      variant="outlined"
                      defaultValue={initialValues && initialValues.name}
                      error={Boolean(errors.name)}
                      onChange={handleChange}
                      className={classes.input}
                      disabled={editedProduct && !editedProduct.ableToEdit}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="marketLabel"
                      defaultValue={initialValues && initialValues.marketLabel}
                      label={t('Market label')}
                      variant="outlined"
                      error={Boolean(errors.marketLabel)}
                      onChange={handleChange}
                      className={classes.input}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" component="span">
                      {t('Description:')}
                    </Typography>
                    <Box mt={1}>
                      <TextEditor
                        placeholder={t('Description')}
                        error={Boolean(errors.description)}
                        value={values.description}
                        onChange={(value) => setFieldValue('description', value)}
                      />
                    </Box>
                  </Grid>
                  <Grid container>
                    <Grid item xs={8}>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Documents:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center">
                            <ShopProductDocumentsModal
                              documents={editedProduct && editedProduct.documents}
                              open={showDocumentsModal}
                              setFieldValue={setFieldValue}
                              onClose={toggleDocumentsModal}
                            />
                            <RoundButton
                              label={editedProduct ? t('Remove & Upload') : t('Upload')}
                              onClick={toggleDocumentsModal}
                              marginRight
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {checkDocuments(errors).length !== 0 && (
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item>
                            <Grid container alignItems="center">
                              <Typography
                                variant="body1"
                                component="span"
                                className={clsx(classes.documents, {
                                  [classes.error]: checkDocuments(errors).length !== 0,
                                })}
                              >
                                {checkDocuments(errors)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {editedProduct && editedProduct.documents.length !== 0 && (
                        <Box mt={2}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography variant="body1" component="span">
                                {t('Uploaded documents:')}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Grid container alignItems="center">
                                {editedProduct.documents.map((item) => (
                                  <Chip
                                    key={item.id}
                                    className={clsx(classes.documentsButton, {
                                      [classes.documentButtonToRemove]: shouldBeRemoved(
                                        values,
                                        item.type,
                                      ),
                                    })}
                                    onClick={() => downloadDocument(item.id, item.name)}
                                    onDelete={() => removeDocument(item.type, setFieldValue)}
                                    label={item.name}
                                  />
                                ))}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      <Grid item xs={12}>
                        <Grid container alignItems="center">
                          <Grid item>
                            <Typography variant="body1" component="span">
                              {t('Free trial:')}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Checkbox
                              name="isFreeTrial"
                              defaultChecked={initialValues && initialValues.isFreeTrial}
                              onChange={(event) =>
                                setFieldValue('isFreeTrial', event.target.checked)
                              }
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center">
                          <Grid item>
                            <Typography variant="body1" component="span">
                              {t('Active product:')}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Checkbox
                              name="active"
                              defaultChecked={initialValues && initialValues.active}
                              onChange={(event) => setFieldValue('active', event.target.checked)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center">
                          <Grid item>
                            <Typography variant="body1" component="span">
                              {t('Specify price for every tier:')}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Checkbox
                              checked={multiplePrice}
                              onChange={(event) => setMultiplePrice(event.target.checked)}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {multiplePrice ? (
                        <Grid item xs={12}>
                          {productProperties.data.tiers.map((tier, index) => (
                            <Grid container alignItems="center" spacing={2} key={tier.id}>
                              <Grid item>
                                <Typography variant="body1" component="span">
                                  {tier.name}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <TextField
                                  name="price"
                                  label={t('Price')}
                                  type="number"
                                  variant="outlined"
                                  defaultValue={multiplePrice && prices[index]}
                                  error={
                                    multiplePrice &&
                                    (typeof prices[index] === 'undefined' ||
                                      (prices[index] === 0 && values.isFreeTrial))
                                  }
                                  onChange={(event) =>
                                    handleChangeTierPrice(event.target.value, index)
                                  }
                                  className={classes.input}
                                  disabled={editedProduct && !editedProduct.ableToEdit}
                                />
                              </Grid>
                              <Grid item>
                                <TextField
                                  name="activationFee"
                                  label={t('Activation fee')}
                                  type="number"
                                  variant="outlined"
                                  defaultValue={multiplePrice && activationFees[index]}
                                  error={
                                    multiplePrice &&
                                    (typeof activationFees[index] === 'undefined' ||
                                      (prices[index] === 0 && activationFees[index] !== 0))
                                  }
                                  onChange={(event) =>
                                    handleChangeTierFee(event.target.value, index)
                                  }
                                  className={classes.input}
                                  disabled={editedProduct && !editedProduct.ableToEdit}
                                />
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={6}>
                            <TextField
                              name="price"
                              label={t('Price')}
                              type="number"
                              variant="outlined"
                              {...(!multiplePrice &&
                              initialValues &&
                              typeof initialValues.price !== 'undefined'
                                ? { defaultValue: initialValues.price }
                                : {})}
                              error={
                                !multiplePrice &&
                                (typeof values.price === 'undefined' ||
                                  (values.price === 0 && values.isFreeTrial))
                              }
                              onChange={handleChange}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              name="activationFee"
                              label={t('Activation fee')}
                              type="number"
                              variant="outlined"
                              {...(!multiplePrice &&
                              initialValues &&
                              typeof initialValues.activationFee !== 'undefined'
                                ? { defaultValue: initialValues.activationFee }
                                : {})}
                              error={
                                !multiplePrice &&
                                (typeof values.activationFee === 'undefined' ||
                                  (values.price === 0 && values.activationFee !== 0))
                              }
                              onChange={handleChange}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Box mt={2}>
                        <Typography variant="body1" component="span">
                          {t('Product image:')}
                        </Typography>
                        <Box mt={2}>
                          <FileUploader
                            fileUrl={
                              editedProduct &&
                              editedProduct.logo &&
                              `${FULL_API_URL}${editedProduct.logo}`
                            }
                            onChange={(img) => setProductImage(img[0])}
                            error={!productImage && !editedProduct}
                            maxSizeMb={3}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              <Box mt={4}>
                <Grid container justifyContent="flex-end">
                  {!editedProduct && activeStep !== 0 && (
                    <Button
                      onClick={handleBack}
                      className={classes.button}
                      color="primary"
                      variant="outlined"
                    >
                      {t('Back')}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    className={classes.button}
                  >
                    {t('Next')}
                  </Button>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
      {activeStep === 2 && (
        <>
          <Box mt={4}>
            <ShopProduct data={productPreview} preview />
          </Box>
          <Grid container justifyContent="flex-end">
            <Button
              onClick={handleBack}
              className={classes.button}
              color="primary"
              variant="outlined"
            >
              {t('Back')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={create}
              className={classes.button}
              disabled={isLoading}
            >
              {editedProduct ? t('Update package') : t('Create package')}
            </Button>
          </Grid>
        </>
      )}
      {activeStep === 3 && (
        <Box p={5}>
          <Grid container justifyContent="center" alignItems="center" direction="column">
            <Grid item>
              <CheckCircleOutlineIcon className={classes.finishIcon} />
            </Grid>
            <Grid item>
              <Typography variant="h5">
                {editedProduct
                  ? t('Package successfully edited')
                  : t('Package successfully created')}
              </Typography>
            </Grid>
            <Grid item>
              <Box pt={3}>
                <LinkButton
                  to="/subscriptions/panel"
                  label={t('View product list')}
                  color="primary"
                  variant="contained"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

ShopPanelPackageCreator.defaultProps = {
  product: null,
};

ShopPanelPackageCreator.propTypes = {
  product: PropTypes.object,
};

export { ShopPanelPackageCreator };
